/**
 * group-storage.ts — Group 状态 localStorage 持久化
 * 
 * localStorage 仅缓存 group 的本地 UI 状态，不能反向重建 DB 中缺失的 group 容器。
 * 画布 group 拓扑以持久化节点数据为准；这里的缓存只用于补充折叠态等本地交互状态。
 */

export interface GroupState {
  childIds: string[];
  collapsed: boolean;
  label: string;
  position: { x: number; y: number };
  thumbnail?: string;
}

const STORAGE_PREFIX = 'octolab:groups:';

function getKey(projectId: string): string {
  return `${STORAGE_PREFIX}${projectId}`;
}

/** 读取项目的所有 group */
export function loadGroups(projectId: string): Record<string, GroupState> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(getKey(projectId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** 保存所有 group（全量覆盖） */
function saveGroups(projectId: string, groups: Record<string, GroupState>): void {
  if (typeof window === 'undefined') return;
  try {
    if (Object.keys(groups).length === 0) {
      localStorage.removeItem(getKey(projectId));
    } else {
      localStorage.setItem(getKey(projectId), JSON.stringify(groups));
    }
  } catch {
    // localStorage 满了或不可用，静默失败
  }
}

/** 添加一个 group */
export function saveGroup(projectId: string, groupId: string, state: GroupState): void {
  const groups = loadGroups(projectId);
  groups[groupId] = state;
  saveGroups(projectId, groups);
}

/** 删除一个 group */
export function removeGroup(projectId: string, groupId: string): void {
  const groups = loadGroups(projectId);
  delete groups[groupId];
  saveGroups(projectId, groups);
}

/** 更新 group 的 collapsed 状态 */
export function updateGroupCollapsed(projectId: string, groupId: string, collapsed: boolean): void {
  const groups = loadGroups(projectId);
  if (groups[groupId]) {
    groups[groupId].collapsed = collapsed;
    saveGroups(projectId, groups);
  }
}
